import React from "react";
import styled, { keyframes, css } from "styled-components/macro";
import { useViewerDecks } from "queries/viewerQuery";
import useOnboardingData from "hooks/useOnboardingData";
import { useSourceCardQuery } from "queries/sources/sourceCardQuery";
import SourceCard from "root/library/library/SourceCard";
import OnboardingRedirect from "./OnboardingRedirect";
import { StepScreenId } from "./onboardingTypes";
import Loader from "components/other/Loader";
import { useABTest } from "queries/users/useABTest";
import { useOpenCourseView } from "root/library/source/course/courseHooks";
import { useGetState } from "components/ReduxProvider";
import useLangNative from "hooks/useLangNative";
import { SOURCE } from "root/library/library/libraryUrls";
import { useNavigate } from "react-router-dom";

type Props = {
  type: "setOrCourse" | "video" | "article";
  skipStraightToDetail?: boolean;
};

const shakeKF = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
`;

const Wrapper = styled.div<{ $shake?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(100% - 4px);
  height: 100%;
  position: relative;
  ${({ $shake }) =>
    $shake &&
    css`
      animation: ${shakeKF} 0.15s ease-in-out;
      animation-iteration-count: 2;
      transform-origin: center;
      will-change: transform;
    `}
`;

const PreparedSource: React.FC<Props> = ({ type, skipStraightToDetail = false }) => {
  const { deck } = useViewerDecks();
  const { getOnboardingData } = useOnboardingData();
  const { setOrCourseId, videoId, articleId } = getOnboardingData().preparedSources;
  const sourceId = type === "video" ? videoId : type === "article" ? articleId : setOrCourseId;
  const { source, error } = useSourceCardQuery(sourceId || "", deck?.back ?? undefined);
  const [shouldShake, setShouldShake] = React.useState(false);
  const noVideoTutorial = useABTest("abtest_noVideoTutorial");
  const openCourseView = useOpenCourseView();
  const langNative = useLangNative();
  const libTopic = useGetState("libTopic");
  const navigate = useNavigate();

  React.useEffect(() => {
    if (source && skipStraightToDetail) {
      if (source.course) {
        openCourseView(source.uri, langNative, libTopic || "");
      } else if (source.kind === "set") {
        navigate(SOURCE.url(source.uri, langNative), { replace: true });
      }
    }
  }, [source, skipStraightToDetail, openCourseView, langNative, libTopic, navigate]);

  // sources not prepared
  if (sourceId === undefined) return <OnboardingRedirect screenId={StepScreenId.COURSE_PREPARATION} />;
  // source not found or error

  if (noVideoTutorial && type === "video") {
    if (videoId) {
      // skip video and article tutorial
      return <OnboardingRedirect screenId={StepScreenId.START_LEARNING} />;
    } else {
      //if there is no video, show article tutorial
      return <OnboardingRedirect screenId={StepScreenId.PREPARED_ARTICLE} />;
    }
  }

  if (!sourceId || error) return <OnboardingRedirect screenId={StepScreenId.START_LEARNING} />;

  const handleClick = (e: React.MouseEvent) => {
    // Check if click target is outside of SourceCard
    const sourceCard = (e.target as HTMLElement).closest("[data-id]");
    if (!sourceCard) {
      // Only check if we clicked outside
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling
      setShouldShake(true);
      // Force a re-render by using a callback
      setTimeout(() => {
        setShouldShake(false);
      }, 1000);
    }
  };

  return (
    <Wrapper $shake={shouldShake} onClick={handleClick}>
      {source && !skipStraightToDetail ? <SourceCard highlight={true} source={source} /> : <Loader />}
    </Wrapper>
  );
};

export default PreparedSource;
